import { AlertTriangle, RefreshCw, Home, Bug, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface ErrorFallbackProps {
  /** The error that was caught */
  error?: Error | null;
  /** Custom title for the error message */
  title?: string;
  /** Custom description for the error message */
  description?: string;
  /** Callback function called when retry is clicked */
  onRetry?: () => void;
  /** Callback function called when report is clicked */
  onReport?: () => void;
  /** Whether to show the back to dashboard button */
  showBackButton?: boolean;
  /** Custom class name for the container */
  className?: string;
  /** Whether this is a full page error or inline error */
  fullPage?: boolean;
}

/**
 * ErrorFallback component that displays a user-friendly error message
 * with options to retry, report, or navigate back to safety.
 */
export default function ErrorFallback({
  error,
  title,
  description,
  onRetry,
  onReport,
  showBackButton = true,
  className,
  fullPage = true,
}: Readonly<ErrorFallbackProps>) {
  const { t } = useTranslation('errors');
  const [showDetails, setShowDetails] = useState(false);

  const resolvedTitle = title ?? t('boundary.title');
  const resolvedDescription = description ?? t('boundary.description');

  const handleBackToDashboard = () => {
    // Navigate to dashboard - works with react-router
    window.location.href = '/app';
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const containerClasses = cn(
    'flex items-center justify-center',
    fullPage ? 'min-h-[60vh] p-6' : 'p-4',
    className
  );

  return (
    <div className={containerClasses}>
      <Card className="w-full max-w-lg shadow-lg border-destructive/20">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-xl font-semibold text-foreground">
            {resolvedTitle}
          </CardTitle>
          <CardDescription className="text-muted-foreground mt-2">
            {resolvedDescription}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Error Details (collapsible in development) */}
          {error && process.env.NODE_ENV === 'development' && (
            <div className="rounded-lg border border-border bg-muted/30">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="w-full flex items-center justify-between p-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={showDetails}
                aria-controls="error-details"
              >
                <span className="flex items-center gap-2">
                  <Bug className="w-4 h-4" />
                  {t('boundary.devDetails')}
                </span>
                {showDetails ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showDetails && (
                <div
                  id="error-details"
                  className="px-3 pb-3 space-y-2"
                >
                  <div className="p-3 rounded-md bg-destructive/5 border border-destructive/10">
                    <p className="text-sm font-mono text-destructive break-words">
                      {error.message}
                    </p>
                  </div>

                  {error.stack && (
                    <div className="max-h-48 overflow-auto">
                      <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap break-words p-3 rounded-md bg-muted/50">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Helpful suggestions */}
          <div className="rounded-lg border border-border bg-card p-4">
            <h4 className="text-sm font-medium text-foreground mb-2">
              {t('boundary.suggestionsTitle')}
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>{t('boundary.suggestionRetry')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>{t('boundary.suggestionRefresh')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>{t('boundary.suggestionClearCache')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                <span>{t('boundary.suggestionSupport')}</span>
              </li>
            </ul>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 pt-2">
          <div className="flex flex-wrap gap-2 w-full justify-center">
            {onRetry && (
              <Button
                onClick={onRetry}
                variant="default"
                className="flex items-center gap-2"
                aria-label={t('boundary.retry')}
              >
                <RefreshCw className="w-4 h-4" />
                {t('boundary.retry')}
              </Button>
            )}

            <Button
              onClick={handleRefreshPage}
              variant="outline"
              className="flex items-center gap-2"
              aria-label={t('boundary.refreshPage')}
            >
              <RefreshCw className="w-4 h-4" />
              {t('boundary.refreshPage')}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 w-full justify-center">
            {showBackButton && (
              <Button
                onClick={handleBackToDashboard}
                variant="ghost"
                className="flex items-center gap-2"
                aria-label={t('boundary.backToDashboard')}
              >
                <Home className="w-4 h-4" />
                {t('boundary.backToDashboard')}
              </Button>
            )}

            {onReport && (
              <Button
                onClick={onReport}
                variant="ghost"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                aria-label={t('boundary.report')}
              >
                <Bug className="w-4 h-4" />
                {t('boundary.report')}
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Inline version of ErrorFallback for smaller sections
 */
export function InlineErrorFallback({
  error,
  onRetry,
  className,
}: Readonly<Pick<ErrorFallbackProps, 'error' | 'onRetry' | 'className'>>) {
  const { t } = useTranslation('errors');
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center p-6 rounded-lg border border-destructive/20 bg-destructive/5',
        className
      )}
    >
      <AlertTriangle className="w-8 h-8 text-destructive mb-3" />
      <p className="text-sm text-muted-foreground text-center mb-4">
        {t('boundary.inlineLoadError')}
        {error && process.env.NODE_ENV === 'development' && (
          <span className="block mt-1 text-xs font-mono text-destructive">
            {error.message}
          </span>
        )}
      </p>
      {onRetry && (
        <Button
          onClick={onRetry}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          aria-label={t('boundary.retry')}
        >
          <RefreshCw className="w-3 h-3" />
          {t('boundary.retry')}
        </Button>
      )}
    </div>
  );
}
